import {
  CLERK_AI_MAX_WIDTH,
  CLERK_AI_MIN_APP_WIDTH,
  CLERK_AI_MIN_WIDTH,
} from '@/constants/clerkAiConstants';
import type {
  ClerkAiAskResponse,
  ClerkAiInlineSegment,
  ClerkAiSummaryBlock,
  ClerkAiTable,
} from '@/models/clerkAiTypes';

export function parseInline(text: string): ClerkAiInlineSegment[] {
  const segments: ClerkAiInlineSegment[] = [];
  const pattern = /\*\*(.+?)\*\*/g;
  let last = 0;
  let match = pattern.exec(text);
  while (match) {
    if (match.index > last)
      segments.push({ text: text.slice(last, match.index), bold: false });
    segments.push({ text: match[1], bold: true });
    last = match.index + match[0].length;
    match = pattern.exec(text);
  }
  if (last < text.length)
    segments.push({ text: text.slice(last), bold: false });
  return segments;
}

/** The AI service's summary is light markdown: paragraphs, `-`/`*` and `1.` lists, `#` headings and `**bold**`. */
export function parseSummary(markdown: string): ClerkAiSummaryBlock[] {
  const blocks: ClerkAiSummaryBlock[] = [];
  let list: Extract<ClerkAiSummaryBlock, { type: 'list' }> | null = null;

  (markdown || '').split(/\r?\n/).forEach((rawLine) => {
    const line = rawLine.trim();
    const bullet = line.match(/^[-*]\s+(.*)$/);
    const numbered = line.match(/^(\d+)[.)]\s+(.*)$/);
    if (bullet || numbered) {
      const ordered = !!numbered;
      if (!list || list.ordered !== ordered) {
        list = {
          type: 'list',
          ordered,
          start: numbered ? Number(numbered[1]) : 1,
          items: [],
        };
        blocks.push(list);
      }
      list.items.push(parseInline(numbered?.[2] ?? bullet?.[1] ?? ''));
      return;
    }
    list = null;
    if (!line) return;

    const heading =
      line.match(/^#{1,6}\s+(.*)$/) || line.match(/^\*\*(.+)\*\*$/);
    if (heading) {
      blocks.push({ type: 'heading', segments: parseInline(heading[1]) });
      return;
    }
    blocks.push({ type: 'paragraph', segments: parseInline(line) });
  });

  return blocks;
}

function hasProse(response: ClerkAiAskResponse): boolean {
  return Boolean(
    response.formatted_output || response.message || response.answer,
  );
}

export function toTable(response: ClerkAiAskResponse): ClerkAiTable | null {
  const data = response.query_results?.data;
  const rows = data?.rows ?? [];
  if (rows.length === 0) return null;
  // A single row is already stated in the summary, when there is one.
  if (rows.length === 1 && hasProse(response)) return null;
  const columns = (data?.cols ?? []).map(
    (col) => col.display_name || col.name || '',
  );
  return { columns, rows };
}

export function toSummary(response: ClerkAiAskResponse): string {
  if (response.formatted_output) return response.formatted_output;
  if (response.message) return response.message;
  if (response.answer) return response.answer;
  if (response.query_results?.error)
    return "ClerkAI couldn't run that question against your data.";
  if (response.query_results?.data?.rows?.length) return '';
  return 'The question ran but found no results.';
}

export function formatCell(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'number') {
    return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
  }
  return String(value);
}

/** Keeps the panel between its minimum and maximum width while leaving the app at least CLERK_AI_MIN_APP_WIDTH. */
export function clampPanelWidth(width: number, viewportWidth: number): number {
  const max = Math.max(
    CLERK_AI_MIN_WIDTH,
    Math.min(CLERK_AI_MAX_WIDTH, viewportWidth - CLERK_AI_MIN_APP_WIDTH),
  );
  return Math.round(Math.min(Math.max(width, CLERK_AI_MIN_WIDTH), max));
}

/** Repeated column names get a suffix; the CSV library keys each row by header and would drop one. */
export function uniqueHeaders(columns: string[]): string[] {
  const seen = new Map<string, number>();
  return columns.map((column, index) => {
    const name = column || `Column ${index + 1}`;
    const count = (seen.get(name) ?? 0) + 1;
    seen.set(name, count);
    return count === 1 ? name : `${name} (${count})`;
  });
}

/** Raw values for the CSV, so totals reconcile; text that a spreadsheet would run as a formula is quoted. */
export function toCsvCell(value: unknown): unknown {
  if (value === null || value === undefined) return '';
  if (typeof value !== 'string') return value;
  // A negative number sent as text ("-125.50") stays a number, or Excel leaves it out of SUM.
  const formulaLike =
    /^[=+@\t\r]/.test(value) ||
    (value.startsWith('-') && Number.isNaN(Number(value)));
  return formulaLike ? `'${value}` : value;
}

export function csvColumnMap(
  table: ClerkAiTable,
): Record<string, (row: unknown[]) => unknown> {
  return uniqueHeaders(table.columns).reduce<
    Record<string, (row: unknown[]) => unknown>
  >((map, header, index) => {
    map[header] = (row) => toCsvCell(row[index]);
    return map;
  }, {});
}

/** clerkai-<question slug>-<YYYY-MM-DD>; the export service adds ".csv". */
export function csvFilename(question: string, now: Date = new Date()): string {
  const slug = question
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .replace(/-+$/, '');
  const pad = (n: number) => String(n).padStart(2, '0');
  const date = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
    now.getDate(),
  )}`;
  return ['clerkai', slug, date].filter(Boolean).join('-');
}

/** The answer as plain text for the clipboard: markdown markers dropped, the table tab-separated so it pastes into a spreadsheet. */
export function toPlainText(
  summary: string,
  table: ClerkAiTable | null,
): string {
  const text = (segments: ClerkAiInlineSegment[]) =>
    segments.map((segment) => segment.text).join('');
  const lines = parseSummary(summary).map((block) => {
    if (block.type !== 'list') return text(block.segments);
    return block.items
      .map((item, index) =>
        block.ordered
          ? `${block.start + index}. ${text(item)}`
          : `- ${text(item)}`,
      )
      .join('\n');
  });
  if (table) {
    const tsv = [table.columns, ...table.rows]
      .map((row) =>
        row
          .map((cell) =>
            cell === null || cell === undefined ? '' : String(cell),
          )
          .join('\t'),
      )
      .join('\n');
    lines.push(tsv);
  }
  return lines.join('\n\n');
}
